<template>
  <BbDashboardCard class="amp-card" fill-width>
    <template #image>
      <div class="thumbnail-wrapper" @click="onSelectImage" v-if="thumbnail">
        <div class="image-label">Image {{ index }}</div>
        <v-img :src="thumbnail" width="100%" max-height="168" contain :style="backgroundStyle" />
      </div>
      <div v-else class="missing-thumbnail-wrapper" @click="onSelectImage">
        <div class="image-label">Image {{ index }}</div>
        <BbIcon class="images-icon" name="medialibrary" />
      </div>
    </template>

    <div class="bb-dashboard-card__row">
      <div class="amp-labels">IMAGE</div>

      <div class="last-updated">{{ modifiedAt }}</div>
    </div>

    <EditableField :value="title" :is-editing="false" />

    <template #footer>
      <div class="bb-dashboard-card__row" data-app>
        <div>
          <BbTooltip v-bind="{ placement: 'top', offset: 4, isDisabled: false, content: t('common.download') }">
            <BbBtn size="small" icon="download" icon-size="small" is-icon-button @click="onDownload(image.id)" />
          </BbTooltip>
          <BbBtn size="small" icon="delete" icon-size="small" is-icon-button @click="confirmDeletePopupOpened = true" />
        </div>
      </div>
    </template>
  </BbDashboardCard>
  <BbPopup v-model="showPreviewModal" width="800px">
    <UserImagePreview
      :image-id="image.id"
      @close="showPreviewModal = false"
      @download="onDownload(image.id)"
      @remix="(imgObject) => onRemix(imgObject)"
    />
  </BbPopup>
  <WarningPopup
    :popup-opened="confirmDeletePopupOpened"
    title-key="common.delete"
    warning-message-key="modals.warningDeleteMessage"
    confirm-text-key="modals.confirmDeleteText"
    @update:open="confirmDeletePopupOpened = false"
    @execute:action="confirmDelete(image.id)"
  />
</template>

<script>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router/dist/vue-router';

import { SetQueryValue } from '~/constants/mutations';
import { Routes } from '~/constants/routes';

import { calculatePassedTime } from '~/utils/date-utils';
import useImageGeneration from '~/composables/useImageGeneration';

import EditableField from '~/components/common/EditableField.vue';
import WarningPopup from '~/components/common/WarningPopup.vue';
import UserImagePreview from '~/components/userImages/UserImagePreview.vue';

export default {
  components: { UserImagePreview, WarningPopup, EditableField },
  emits: ['delete-image'],
  props: {
    image: {
      type: Object,
      default: () => ({}),
    },
    index: {
      type: Number,
      default: 0,
    },
  },
  setup(props, { emit }) {
    const router = useRouter();
    const { t } = useI18n();
    const store = useStore();

    const { downloadUserImage, setUserImageForRemix } = useImageGeneration();

    const title = ref(props.image?.prompt || 'Generetad Image');
    const confirmDeletePopupOpened = ref(false);
    const showPreviewModal = ref(false);

    const thumbnail = computed(() => props.image?.url || props.image?.uploadUrl);
    const backgroundStyle = computed(() => ({ background: `url(${thumbnail.value}) lightgray 50% / cover no-repeat` }));
    const modifiedAt = computed(() => (props.image?.createdAt ? calculatePassedTime(props.image?.createdAt) : null));

    const confirmDelete = async (imageId) => {
      emit('delete-image', imageId);
    };

    const onRemix = async (imgObject) => {
      await router.push({ name: Routes.Conversation.name });
      const userRemixObject = {
        query: imgObject.userQuery,
        remixImage: imgObject.url,
        remixImageId: imgObject.id,
      };
      setUserImageForRemix({ ...imgObject, ...userRemixObject });
      // Set the query value after navigation completes
      store.dispatch(`conversationStore/${SetQueryValue}`, userRemixObject);
    };

    const onDownload = async (id) => {
      if (thumbnail.value) {
        const imageName = props.image.name || 'Generetad_Image.png';
        await downloadUserImage(id, imageName);
      }
    };

    const onSelectImage = () => {
      showPreviewModal.value = true;
    };

    return {
      backgroundStyle,
      onSelectImage,
      onDownload,
      onRemix,
      title,
      thumbnail,
      t,
      modifiedAt,
      confirmDelete,
      confirmDeletePopupOpened,
      showPreviewModal,
    };
  },
};
</script>

<style lang="scss" scoped>
.image-label {
  width: fit-content;
  background: $bb-neutral-0;
  padding: 0 10px;
  border-radius: 5px 100px 100px 100px;
  position: absolute;
  left: 0;
  top: 0;
  z-index: 2;
  height: $bb-spacing-4;
  min-width: $bb-spacing-14;
  line-height: $bb-spacing-4;
  font-size: 11px;
}

.amp-card {
  @include bb-base-font-family;
  position: relative;
  flex: 1 1 271px;
  max-width: 321px !important;
  width: 291px !important;
  height: 288px !important; // use this when footer will be added back
  //height: 262px !important;

  .amp-labels {
    display: flex;
    flex-direction: row;
    padding: 0px 8px;
    justify-content: center;
    align-items: center;
    border-radius: 8px;
    width: fit-content;
    background: #e39250;
    font-size: 11px;
    line-height: 16px;
    letter-spacing: 0.25px;
    color: $bb-neutral-0;
  }

  .last-updated {
    @include bb-regular-text-styles('xs');
    color: $bb-neutral-2;
  }

  .thumbnail-wrapper {
    position: relative;
    overflow: hidden;
    border-radius: 3px 3px 0 0;
    cursor: pointer;
    min-height: 154px;

    :deep(.v-img__img) {
      backdrop-filter: blur(12.5px);
      padding-top: 7px;
    }
  }

  .missing-thumbnail-wrapper {
    position: relative;
    background: $bb-neutral-5;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    height: 168px;

    .images-icon {
      font-size: 66px;
    }
  }

  .bb-dashboard-card__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 4px;

    .bb-btn.primary {
      width: 32px;
      height: 32px;
      border-radius: 12px;
      background: $bb-neutral-6 !important;
      color: #1c1c1d !important;

      &:hover {
        background: $bb-neutral-6 !important;
      }

      &:nth-of-type(2) {
        background: $bb-interface-5 !important;
        color: $bb-neutral-0 !important;
        margin-left: $bb-spacing-1;
      }
    }
  }

  &:hover {
    box-shadow: 0px 5px 8px rgba(0, 0, 0, 0.1);
  }
}
</style>
